import { useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  MinimalDevice,
  NetworkInterface,
} from '@rtkwlf/asset-data-service';
import { faChevronRight } from '@rtkwlf/pro-solid-svg-icons/faChevronRight';
import dayjs from 'dayjs';
import noop from 'lodash/noop';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation } from 'react-router-dom';

import { EnumCell } from '@packages/components/column-store/cell/enum-cell/enum-cell';
import { LinkCell } from '@packages/components/column-store/cell/link-cell/link-cell';
import { StringCell } from '@packages/components/column-store/cell/string-cell/string-cell';
import { assetCriticalityColumnType } from '@packages/components/column-store/column/asset-criticality-column-type';
import { datetimeColumnType } from '@packages/components/column-store/column/datetime-column-type';
import { longStringColumnType } from '@packages/components/column-store/column/long-string-column-type';
import { mediumStringColumnType } from '@packages/components/column-store/column/medium-string-column-type';
import { riskScoreColumnType } from '@packages/components/column-store/column/risk-score-column-type';
import { singleSelectColumnType } from '@packages/components/column-store/column/single-select-column-type';
import { smallStringColumnType } from '@packages/components/column-store/column/small-string-column-type';
import { NoDataEnDash } from '@packages/components/no-data-en-dash/no-data-en-dash';
import { TagsDisplay } from '@packages/components/tags-display';
import {
  Stack,
  Typography,
  getFilteredGridStringOperators,
  GridStringOperator,
  LinkRouter,
  Button,
  List,
  ListItem,
} from '@packages/fenrir/components';
import type {
  GridColDef,
  GridRowSelectionModel,
} from '@packages/fenrir/components/datagrid';
import { useOrganizationContext } from '@packages/legacy/organization-context';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';
import { ADS_ROUTE_ASSETS } from 'ae-constants/routes.constants';

import { fieldFilterLocaleKeys } from '../../../assets/device-list/asset-filters/asset-type-fields-filters';
import { useAssetTypeParam } from '../../../assets/device-list/use-asset-type-param';
import { getMaxLastSuccessfulScan } from '../../get-max-last-successful-scan/get-max-last-successful-scan';
import { RiskLink } from '../../risk-link/risk-link';
import type { AssetsTableColumnId } from '../asset-table.constants';
import { ASSET_COLUMNS_DEFS } from '../asset-table.constants';
import { EllipsisMenu } from '../ellipsis-menu/ellipsis-menu';
import { useAssetFilterOptions } from '../use-asset-filter-options/use-asset-filter-options';

export const useAssetsTableColumns = (
  selectedColumns?: AssetsTableColumnId[],
  setRowSelectionModel?: (model: GridRowSelectionModel) => void,
) => {
  const { t } = useTranslation('ads');
  const location = useLocation();
  const assetTypeParam = useAssetTypeParam();
  const [risksWIP] = useFeatureFlag('RISKS_IN_UP_WIP');
  const { filterOptions } = useAssetFilterOptions();

  const { selectedOrganization } = useOrganizationContext();

  const columns: GridColDef[] = useMemo(
    () =>
      (
        [
          {
            ...mediumStringColumnType,
            field: ASSET_COLUMNS_DEFS.NAME,
            headerName: t('fields.deviceName'),
            filterOperators: getFilteredGridStringOperators([
              GridStringOperator.Contains,
            ]),
            renderCell: (params) => {
              return (
                <LinkCell
                  {...params}
                  to={generatePath(
                    `/assets/${assetTypeParam}/${params.row.id}`,
                  )}
                />
              );
            },
          },
          {
            ...singleSelectColumnType,
            field: ASSET_COLUMNS_DEFS.CATEGORY,
            headerName: t(fieldFilterLocaleKeys.categories),
            valueOptions: filterOptions[ASSET_COLUMNS_DEFS.CATEGORY],
            renderCell: (params) => (
              <EnumCell {...params} translationKey="ads:category" />
            ),
          },
          {
            ...singleSelectColumnType,
            field: ASSET_COLUMNS_DEFS.TAGS,
            headerName: t(fieldFilterLocaleKeys.tags),
            sortable: false,
            minWidth: 200,
            valueOptions: filterOptions[ASSET_COLUMNS_DEFS.TAGS],
            renderCell: ({ row }) => {
              // TODO: this should be converted to a column-store cell
              // in @packages/components/column-store/cell/
              const tags = row.tags;

              if (!tags?.length) return <NoDataEnDash />;

              return (
                <TagsDisplay
                  shouldTruncateTags
                  tags={tags.map((tag) => ({
                    children: tag,
                    variant: 'secondary',
                  }))}
                  max={3}
                />
              );
            },
          },
          {
            ...smallStringColumnType,
            field: ASSET_COLUMNS_DEFS.RISK_COUNT,
            headerName: t('fields.riskCount'),
            renderCell: (params) => (
              // TODO: this should be converted to a column-store cell
              // in @packages/components/column-store/cell/
              <RiskLink
                value={params.row.risk?.count}
                assetId={params.row.id}
              />
            ),
            minWidth: 105,
            filterable: false,
          },
          {
            ...mediumStringColumnType,
            field: ASSET_COLUMNS_DEFS.LEGACY_DEVICE_IDS,
            headerName: t('fields.legacyDeviceCount'),
            valueGetter: (_value, row: MinimalDevice) =>
              row.legacyDeviceIds?.length,
            renderCell: (params) => {
              if (!params.value) return <NoDataEnDash />;
              return (
                <LinkCell
                  {...params}
                  to={generatePath(
                    `/assets?id=${selectedOrganization.id}&adsAssetId=${params.row.id}`,
                    {
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    },
                  )}
                />
              );
            },
            filterable: false,
          },
          {
            ...singleSelectColumnType,
            field: ASSET_COLUMNS_DEFS.SOURCES,
            headerName: t(fieldFilterLocaleKeys.sources),
            renderCell: (params) => {
              // TODO: this should be converted to a column-store cell
              // in @packages/components/column-store/cell/
              const sources = params.row.sources;

              if (!sources) return <NoDataEnDash />;

              return (
                <List sx={{ width: '100%' }}>
                  {sources.map((source) => (
                    <ListItem disablePadding>
                      <EnumCell
                        {...{ ...params, value: source }}
                        translationKey="ads:source"
                      />
                    </ListItem>
                  ))}
                </List>
              );
            },
            minWidth: 100,
            sortable: false,
            valueOptions: filterOptions[ASSET_COLUMNS_DEFS.SOURCES],
          },
          {
            ...riskScoreColumnType,
            field: ASSET_COLUMNS_DEFS.RISK_SCORE,
            valueGetter: (_value, row) => row.risk?.score,
            headerName: t('fields.riskScore'),
            minWidth: 100,
          },
          {
            ...assetCriticalityColumnType,
            field: ASSET_COLUMNS_DEFS.IMPORTANCE,
            headerName: t(fieldFilterLocaleKeys.importances),
            minWidth: 150,
          },
          {
            ...datetimeColumnType,
            field: ASSET_COLUMNS_DEFS.LAST_SUCCESSFUL_SCANNED,
            headerName: t(fieldFilterLocaleKeys.lastScan),
            valueGetter: (_value, row) => {
              const val = getMaxLastSuccessfulScan(
                row.risk?.lastSuccessfulScanned,
              );
              const date = dayjs(val);
              return date.isValid() && date.year() ? val : undefined;
            },
          },
          {
            ...datetimeColumnType,
            field: ASSET_COLUMNS_DEFS.LAST_SEEN_TIME,
            headerName: t(fieldFilterLocaleKeys.lastSeen),
          },
          {
            ...longStringColumnType,
            field: ASSET_COLUMNS_DEFS.HOSTNAME,
            headerName: t(fieldFilterLocaleKeys.hostname),
            sortable: false,
            filterOperators: getFilteredGridStringOperators([
              GridStringOperator.Contains,
            ]),
          },
          {
            ...longStringColumnType,
            field: ASSET_COLUMNS_DEFS.NETBIOS,
            headerName: t('ads:fields.netbios'),
            sortable: false,
            filterable: false,
          },
          {
            ...longStringColumnType,
            field: ASSET_COLUMNS_DEFS.OPERATING_SYSTEM,
            headerName: t(fieldFilterLocaleKeys.os),
            sortable: false,
            filterOperators: getFilteredGridStringOperators([
              GridStringOperator.Contains,
            ]),
            renderCell: (params) => {
              const { value } = params;
              // TODO: this should be converted to a column-store cell
              // in @packages/components/column-store/cell/
              // Probably in this case need a multi-value string cell type
              if (!value?.name && !value?.version) return <NoDataEnDash />;
              return (
                <List sx={{ width: '100%' }}>
                  {value?.name && (
                    <ListItem disablePadding>
                      <StringCell
                        {...{
                          ...params,
                          value: `${t('assetDetails.information.osDetails.name')} ${value.name}`,
                        }}
                      />
                    </ListItem>
                  )}
                  {value?.version && (
                    <ListItem disablePadding>
                      <StringCell
                        {...{
                          ...params,
                          value: `${t('assetDetails.information.osDetails.version')} ${value.version}`,
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              );
            },
          },
          {
            ...smallStringColumnType,
            field: ASSET_COLUMNS_DEFS.OS_TYPE,
            valueGetter: (_value: unknown, row: MinimalDevice) => row?.os?.type,
            headerName: t('ads:fields.osType'),
            renderCell: (params) => (
              <EnumCell {...params} translationKey="ads:osType" />
            ),
            sortable: false,
            filterable: false,
          },
          {
            ...longStringColumnType,
            field: ASSET_COLUMNS_DEFS.IP_ADDRESS,
            headerName: t(fieldFilterLocaleKeys.ipAddress),
            valueGetter: (_value, row) => row?.networkInterfaces,
            renderCell: ({ value }) => {
              // TODO: this should be converted to a column-store cell
              // in @packages/components/column-store/cell/
              // Probably in this case need a multi-value string cell type
              // XXX should not be showing more than 3 IP addresses in 1 row without truncation
              if (!value) return <NoDataEnDash />;

              const ipAddresses = value
                .map(
                  (networkDetails: NetworkInterface) =>
                    networkDetails.ipAddresses,
                )
                .flat();
              return (
                <List sx={{ width: '100%' }}>
                  {ipAddresses.map((ipAddress: string) => (
                    <ListItem disablePadding>
                      <Typography variant="body2" noWrap key={ipAddress}>
                        {ipAddress}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              );
            },
            sortable: false,
            filterOperators: getFilteredGridStringOperators([
              GridStringOperator.Contains,
            ]),
          },
          {
            ...longStringColumnType,
            field: ASSET_COLUMNS_DEFS.MAC,
            headerName: t(fieldFilterLocaleKeys.mac),
            valueGetter: (_value, row) => row?.networkInterfaces,
            renderCell: ({ value }) => {
              // TODO: this should be converted to a column-store cell
              // in @packages/components/column-store/cell/
              // Probably in this case need a multi-value string cell type
              if (!value) return <NoDataEnDash />;

              const macAddresses = value
                .map((networkDetails: NetworkInterface) => networkDetails.mac)
                .flat();
              return (
                <List sx={{ width: '100%' }}>
                  {macAddresses.map((macAddress: string) => (
                    <ListItem disablePadding>
                      <Typography variant="body2" key={macAddress}>
                        {macAddress}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              );
            },
            sortable: false,
            filterOperators: getFilteredGridStringOperators([
              GridStringOperator.Contains,
            ]),
          },
          {
            field: ASSET_COLUMNS_DEFS.ACTIONS,
            headerName: t('table:actions'),
            type: 'actions',
            width: 180,
            renderCell: ({ row }) => (
              // TODO: this should be converted to a shared component
              <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                <LinkRouter
                  to={{
                    pathname: generatePath(
                      `${ADS_ROUTE_ASSETS}/${assetTypeParam}/${row.id}`,
                    ),
                    search: location.search,
                  }}
                >
                  <Button endIcon={<FontAwesomeIcon icon={faChevronRight} />}>
                    {t('assetsTable.viewAsset')}
                  </Button>
                </LinkRouter>
                {risksWIP && (
                  <EllipsisMenu
                    assetData={[row]}
                    setRowSelectionModel={setRowSelectionModel || noop}
                  />
                )}
              </Stack>
            ),
            sortable: false,
            filterable: false,
            resizable: false,
          },
        ] as GridColDef<MinimalDevice>[]
      ).filter(
        (column) =>
          selectedColumns?.includes(column.field as AssetsTableColumnId) ??
          true,
      ),
    [
      t,
      filterOptions,
      assetTypeParam,
      selectedOrganization.id,
      location.search,
      risksWIP,
      setRowSelectionModel,
      selectedColumns,
    ],
  );

  return columns;
};
